// src/pages/AboutUs.js
import React from "react";
import { Users, Heart, BarChart2, Activity } from "lucide-react"; // Assuming you still want to use these icons

const About = () => {
  return (
    <div className="bg-gray-50 min-h-screen pb-16">
      {" "}
      {/* Added min-h-screen and pb-16 for better spacing */}
      {/* Header Section - Similar to the Pricing page's top section */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 py-12 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2">
          About Us
        </h1>
        <p className="text-xl text-gray-700">
          Revibe Health and Happiness – India’s Smartest Health & Wellness App
        </p>
      </div>
      {/* Main Content Area - Wrapped in a container that resembles the pricing grid's structure */}
      <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 gap-8">
        {/* Intro Section - Now in a card-like structure */}
        <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Who We Are
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Revibe Health and Happiness is a comprehensive health and wellness
            platform dedicated to empowering individuals to achieve balance in
            fitness, nutrition, and mental well-being. Our goal is to make
            health accessible, convenient, and sustainable for every age group
            by combining expert guidance with smart digital solutions.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Since our inception, we have focused on redefining the way people
            approach wellness. By integrating live and recorded workouts,
            personalized diet plans, mindset care, physiotherapy, and holistic
            wellness programs into one platform, Revibe ensures that individuals
            can take control of their health anytime, anywhere.
          </p>
        </div>

        {/* What We Offer Section - Using a simpler grid, similar to your original pricing card layout for visual consistency */}
        <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
            What We Offer
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
            <div className="flex items-start space-x-4">
              <Activity className="w-6 h-6 text-lime-600 flex-shrink-0" />{" "}
              {/* Adjusted color */}
              <div>
                <h3 className="font-semibold text-lg text-gray-900">
                  Live & Recorded Workouts
                </h3>
                <p className="text-gray-700 text-sm">
                  Professional fitness sessions led by certified trainers,
                  available in real-time or on-demand, including yoga, Zumba,
                  strength training, and more.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <Heart className="w-6 h-6 text-red-600 flex-shrink-0" />{" "}
              {/* Adjusted color */}
              <div>
                <h3 className="font-semibold text-lg text-gray-900">
                  Personal Diet Plans
                </h3>
                <p className="text-gray-700 text-sm">
                  Customized meal plans created by qualified nutritionists to
                  support diverse health goals such as weight loss, muscle
                  building, or general well-being.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <Users className="w-6 h-6 text-blue-600 flex-shrink-0" />{" "}
              {/* Adjusted color */}
              <div>
                <h3 className="font-semibold text-lg text-gray-900">
                  Mindset Care
                </h3>
                <p className="text-gray-700 text-sm">
                  Programs designed to promote mental resilience, stress
                  management, and positive lifestyle habits.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <BarChart2 className="w-6 h-6 text-purple-600 flex-shrink-0" />{" "}
              {/* Adjusted color */}
              <div>
                <h3 className="font-semibold text-lg text-gray-900">
                  Physiotherapy Support
                </h3>
                <p className="text-gray-700 text-sm">
                  Expert physiotherapy guidance to aid recovery, enhance
                  mobility, and prevent injuries.
                </p>
              </div>
            </div>

            {/* Centered for larger screens */}
            <div className="flex items-start space-x-4 md:col-span-2 md:justify-center">
              <Users className="w-6 h-6 text-teal-600 flex-shrink-0" />{" "}
              {/* Adjusted color */}
              <div>
                <h3 className="font-semibold text-lg text-gray-900">
                  Wellness for Every Age
                </h3>
                <p className="text-gray-700 text-sm">
                  Safe and effective wellness programs designed for children,
                  adults, and seniors.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Us Section - Now in a card-like structure */}
        <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
            Why Choose Revibe
          </h2>
          <ul className="list-disc list-inside space-y-3 text-gray-700 px-4">
            <li>
              <span className="font-medium text-gray-900">
                Comprehensive solutions:
              </span>{" "}
              Health and wellness in a single platform.
            </li>
            <li>
              <span className="font-medium text-gray-900">Expert-backed:</span>{" "}
              Certified trainers, nutritionists, and healthcare professionals.
            </li>
            <li>
              <span className="font-medium text-gray-900">
                Personalized programs:
              </span>{" "}
              Adaptable to individual lifestyles.
            </li>
            <li>
              <span className="font-medium text-gray-900">
                Long-term results:
              </span>{" "}
              Focus on fitness, nutrition, and mindset care.
            </li>
            <li>
              <span className="font-medium text-gray-900">
                Supportive community:
              </span>{" "}
              Motivates and inspires your journey.
            </li>
          </ul>
        </div>

        {/* Mission Section - Now in a card-like structure */}
        <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
            Our Mission
          </h2>
          <p className="text-gray-700 leading-relaxed text-center max-w-2xl mx-auto">
            At Revibe Health and Happiness, our mission is to transform the way
            health and wellness are experienced. We aim to deliver innovative,
            reliable, and holistic solutions that help people lead healthier,
            happier, and more fulfilling lives.
          </p>
        </div>
      </div>
    </div>
  );
};

export default About;
